import { useId, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { Popover, PopoverBody, Button } from 'reactstrap';
import { BiCalendar, BiX } from 'react-icons/bi';
import { ymdToLocalDate, localDateToYmd, formatDdMmYyyy } from './dateHelpers';
import CalendarDropdown from './calendarDropdown';

/* ---------- Component --------------------------------------------------- */
export default function DatePicker({
    value,
    handleChange,
    disableWeekends,
    isClearable = true,
    disabled,
    inputStyle,
    fromDate,
    toDate,
    placeholder = 'dd/mm/yyyy',
}) {
    const [open, setOpen] = useState(false);
    // useId gives us a collision-free DOM id for <Popover target="..."/>.
    // Replace the ":" characters React emits so the value is a valid CSS
    // selector (reactstrap passes target through document.querySelector).
    const triggerId = `dp-${useId().replace(/:/g, '_')}`;

    const selected = useMemo(() => ymdToLocalDate(value), [value]);
    const display = value ? formatDdMmYyyy(value) : '';

    const disabledMatcher = useMemo(
        () =>
            [
                disableWeekends && { dayOfWeek: [0, 6] },
                fromDate && { before: ymdToLocalDate(fromDate) },
                toDate && { after: ymdToLocalDate(toDate) },
            ].filter(Boolean),
        [disableWeekends, fromDate, toDate]
    );

    const commit = (ymd) => {
        handleChange(ymd);
        setOpen(false);
    };

    return (
        <>
            <div className="dp-trigger-wrap">
                <button
                    id={triggerId}
                    type="button"
                    className={`form-control dp-trigger${display ? '' : ' is-placeholder'}`}
                    style={inputStyle}
                    disabled={disabled}
                    onClick={() => setOpen((o) => !o)}
                >
                    {display || placeholder}
                </button>
                <div className="dp-trigger__icons">
                    {!!value && isClearable && !disabled && (
                        <button
                            type="button"
                            className="dp-trigger__icon-btn"
                            aria-label="Clear date"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleChange('');
                            }}
                        >
                            <BiX size={16} />
                        </button>
                    )}
                    <BiCalendar size={16} />
                </div>
            </div>

            <Popover
                isOpen={open}
                target={triggerId}
                placement="bottom-start"
                toggle={() => setOpen((o) => !o)}
                trigger="legacy"
                popperClassName="dp-popover"
                hideArrow
                fade={false}
            >
                <PopoverBody>
                    <DayPicker
                        mode="single"
                        selected={selected}
                        onSelect={(d) => commit(d ? localDateToYmd(d) : '')}
                        disabled={disabledMatcher}
                        weekStartsOn={1}
                        captionLayout="dropdown"
                        components={{ Dropdown: CalendarDropdown }}
                    />
                    <div className="dp-footer">
                        <Button
                            size="sm"
                            outline
                            color="secondary"
                            onClick={() => commit(localDateToYmd(new Date()))}
                        >
                            Today
                        </Button>
                        {isClearable && (
                            <Button
                                size="sm"
                                outline
                                color="secondary"
                                onClick={() => commit('')}
                                disabled={!value}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </PopoverBody>
            </Popover>
        </>
    );
}
